import React, { useState, useEffect } from 'react';
import { MdOutlineQueryStats } from "react-icons/md";
import { TrendingUp, TrendingDown, Activity, Calendar, Clock, CheckCircle } from 'lucide-react';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

export default function StatisticsModule({ className = "", layout = "vertical" }) {
  const [stats, setStats] = useState({
    ticketsResolusCeMois: 0,
    ticketsResolusSemaine: 0,
    tempsResolutionMoyen: 0,
    ticketsOuverts: 0,
    tendanceMois: 0,
    tendanceSemaine: 0,
    loading: true
  });

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - 7);

      // Tickets résolus ce mois
      const resolusQuery = query(
        collection(db, 'tickets'),
        where('statut', '==', 'cloture'),
        where('dateCloture', '>=', startOfMonth.toISOString())
      );
      const resolusSnapshot = await getDocs(resolusQuery);
      const ticketsResolusCeMois = resolusSnapshot.size;

      // Tickets résolus cette semaine
      const resolusSemaineQuery = query(
        collection(db, 'tickets'),
        where('statut', '==', 'cloture'),
        where('dateCloture', '>=', startOfWeek.toISOString())
      );
      const resolusSemaineSnapshot = await getDocs(resolusSemaineQuery);
      const ticketsResolusSemaine = resolusSemaineSnapshot.size;

      // Tickets ouverts actuellement
      const ouvertsQuery = query(
        collection(db, 'tickets'),
        where('statut', '==', 'ouvert')
      );
      const ouvertsSnapshot = await getDocs(ouvertsQuery);
      const ticketsOuverts = ouvertsSnapshot.size;

      // Calcul du temps de résolution moyen
      let totalResolutionTime = 0;
      let ticketsWithResolutionTime = 0;
      
      resolusSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.dateCreation && data.dateCloture) {
          const created = new Date(data.dateCreation);
          const closed = new Date(data.dateCloture);
          const diffHours = Math.abs(closed - created) / (1000 * 60 * 60);
          totalResolutionTime += diffHours;
          ticketsWithResolutionTime++;
        }
      });

      const tempsResolutionMoyen = ticketsWithResolutionTime > 0 
        ? Math.round(totalResolutionTime / ticketsWithResolutionTime)
        : 0;

      // Calcul de la tendance (comparaison avec le mois précédent)
      const resolusMoisPrecedentQuery = query(
        collection(db, 'tickets'),
        where('statut', '==', 'cloture'),
        where('dateCloture', '>=', startOfLastMonth.toISOString()),
        where('dateCloture', '<', startOfMonth.toISOString())
      );
      const resolusMoisPrecedentSnapshot = await getDocs(resolusMoisPrecedentQuery);
      const ticketsResolusMoisPrecedent = resolusMoisPrecedentSnapshot.size;

      const tendanceMois = ticketsResolusMoisPrecedent > 0
        ? Math.round(((ticketsResolusCeMois - ticketsResolusMoisPrecedent) / ticketsResolusMoisPrecedent) * 100)
        : 0;

      setStats({
        ticketsResolusCeMois,
        ticketsResolusSemaine,
        tempsResolutionMoyen,
        ticketsOuverts,
        tendanceMois,
        tendanceSemaine: 0, // À implémenter si nécessaire
        loading: false
      });

    } catch (error) {
      console.error('Erreur chargement statistiques:', error);
      // En cas d'erreur, on affiche des données de démonstration
      setStats({
        ticketsResolusCeMois: 12,
        ticketsResolusSemaine: 3,
        tempsResolutionMoyen: 24,
        ticketsOuverts: 5,
        tendanceMois: 20,
        tendanceSemaine: 15,
        loading: false
      });
    }
  };

  // Format pour afficher la tendance
  const getTrendIcon = (trend) => {
    if (trend > 0) return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (trend < 0) return <TrendingDown className="h-4 w-4 text-red-600" />;
    return <Activity className="h-4 w-4 text-gray-600" />;
  };

  const getTrendColor = (trend) => {
    if (trend > 0) return 'text-green-700';
    if (trend < 0) return 'text-red-700';
    return 'text-gray-700';
  };

  // Composant pour une carte de statistique
  const StatCard = ({ icon: Icon, value, label, trend, color = "green" }) => {
    const colorClasses = {
      green: "bg-green-100 border-green-300",
      blue: "bg-blue-100 border-blue-300",
      purple: "bg-purple-100 border-purple-300",
      orange: "bg-orange-100 border-orange-300"
    };

    return (
      <div className={`p-4 rounded-lg border-2 ${colorClasses[color]} shadow-md`}>
        <div className="flex items-start justify-between mb-2">
          <Icon className={`h-5 w-5 text-${color}-600`} />
          {trend !== undefined && (
            <div className="flex items-center gap-1">
              {getTrendIcon(trend)}
              <span className={`text-xs font-medium ${getTrendColor(trend)}`}>
                {trend > 0 ? '+' : ''}{trend}%
              </span>
            </div>
          )}
        </div>
        <div className="text-2xl font-bold text-gray-900 mb-1">
          {value}
        </div>
        <p className="text-sm text-gray-700">
          {label}
        </p>
      </div>
    );
  };

  // Layout vertical (pour sidebar)
  if (layout === "vertical") {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="relative bg-green-50 p-4 rounded-lg border-2 border-green-300 overflow-hidden">
          <MdOutlineQueryStats className="absolute right-2 top-1/2 -translate-y-1/2 h-16 w-16 text-green-200 opacity-50" />
          <div className="relative z-10">
            <h3 className="font-bold text-green-800 text-lg">Statistiques</h3>
            <p className="text-sm text-green-600 mt-1">Vue d'ensemble</p>
          </div>
        </div>
        
        <div className="space-y-3">
          {stats.loading ? (
            <div className="text-center py-8">
              <div className="animate-spin h-6 w-6 border-3 border-green-600 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-sm text-gray-600 mt-2">Chargement...</p>
            </div>
          ) : (
            <>
              <StatCard
                icon={CheckCircle}
                value={stats.ticketsResolusCeMois}
                label="Résolus ce mois"
                trend={stats.tendanceMois}
                color="green"
              />
              <StatCard
                icon={Calendar}
                value={stats.ticketsResolusSemaine}
                label="Résolus cette semaine"
                color="blue"
              />
              <StatCard
                icon={Clock}
                value={`${stats.tempsResolutionMoyen}h`}
                label="Temps résolution moyen"
                color="purple"
              />
              <StatCard
                icon={Activity}
                value={stats.ticketsOuverts}
                label="Tickets ouverts"
                color="orange"
              />
            </>
          )}
        </div>
      </div>
    );
  }

  // Layout horizontal (pour bas de page)
  if (layout === "horizontal") {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="relative bg-green-50 p-4 rounded-lg border-2 border-green-300 overflow-hidden">
          <MdOutlineQueryStats className="absolute right-4 top-1/2 -translate-y-1/2 h-16 w-16 text-green-200 opacity-50" />
          <div className="relative z-10">
            <h3 className="font-bold text-green-800 text-lg">Statistiques</h3>
            <p className="text-sm text-green-600 mt-1">Vue d'ensemble</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.loading ? (
            <div className="col-span-full text-center py-8">
              <div className="animate-spin h-6 w-6 border-3 border-green-600 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-sm text-gray-600 mt-2">Chargement...</p>
            </div>
          ) : (
            <>
              <StatCard
                icon={CheckCircle}
                value={stats.ticketsResolusCeMois}
                label="Résolus ce mois"
                trend={stats.tendanceMois}
                color="green"
              />
              <StatCard
                icon={Calendar}
                value={stats.ticketsResolusSemaine}
                label="Résolus cette semaine"
                color="blue"
              />
              <StatCard
                icon={Clock}
                value={`${stats.tempsResolutionMoyen}h`}
                label="Temps résolution moyen"
                color="purple"
              />
              <StatCard
                icon={Activity}
                value={stats.ticketsOuverts}
                label="Tickets ouverts"
                color="orange"
              />
            </>
          )}
        </div>
      </div>
    );
  }
}